/**
 * The inventory grid's geometry, matching .inventory in styles.css: 7 tiles of 42 px, 4 px apart,
 * inside 6 px of padding and a 1 px border. The HUD needs the numbers to keep the whole grid on
 * screen, since it hangs above the active mole, who may be standing at either edge.
 */
export const INV_COLS = 7;
export const INV_TILE = 42;
export const INV_GAP = 4;
export const INV_EDGE = 7;
/** Breathing room between the inventory and the edge of the battlefield; the fold badge sticks out 11 px past a corner. */
export const INV_MARGIN = 12;

/** Size in CSS px of the inventory for a menu of `count` tiles. Folded up it is the tile in hand alone. */
export function inventorySize(count: number, folded: boolean): { w: number; h: number } {
  if (folded) return { w: INV_TILE + 2 * INV_EDGE, h: INV_TILE + 2 * INV_EDGE };
  const rows = Math.max(1, Math.ceil(count / INV_COLS));
  return { w: INV_COLS * INV_TILE + (INV_COLS - 1) * INV_GAP + 2 * INV_EDGE, h: rows * INV_TILE + (rows - 1) * INV_GAP + 2 * INV_EDGE };
}

/** What a folded inventory still shows: the tile in hand, or failing that the first one that can be used. */
export function foldedTile<T extends { id: string; enabled: boolean }>(menu: readonly T[], held: string | null): T | null {
  return menu.find((m) => m.id === held) ?? menu.find((m) => m.enabled && m.id !== 'skip') ?? null;
}

/** The folded inventory as a list for the menu to draw: the tile in hand, or nothing. */
export function foldedTiles<T extends { id: string; enabled: boolean }>(menu: readonly T[], held: string | null): T[] {
  const t = foldedTile(menu, held);
  return t ? [t] : [];
}

const KEY = 'cratered.inventory';

/** Whether the player left the brawl inventory open (the default) or folded it up. Remembered between matches. */
export function loadInventoryOpen(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'folded';
  } catch {
    return true;
  }
}

export function saveInventoryOpen(open: boolean): void {
  try {
    localStorage.setItem(KEY, open ? 'open' : 'folded');
  } catch {
    /* private window or blocked storage: it just is not remembered */
  }
}
